import { useEffect, useRef, useState } from "react";
import { createUseStyles } from "react-jss";
import { HskEntry } from "../helpers/types";
import CompactCard from "./CompactCard";

const DESKTOP_MIN_TILE = 72;
const MOBILE_MIN_TILE = 64;
const DESKTOP_GAP = 8;
const MOBILE_GAP = 6;

const getColumnCount = (width: number, minTile: number, gap: number) =>
  Math.max(1, Math.floor((width + gap) / (minTile + gap)));

const useStyles = createUseStyles({
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(min(72px, 100%), 1fr))",
    gap: 8,
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    "& > *": {
      minWidth: 0
    }
  },
  gridMobile: {
    gridTemplateColumns: "repeat(auto-fill, minmax(min(64px, 100%), 1fr))",
    gap: 6
  }
});

const CompactCardGrid = ({
  cards,
  isMobile = false,
  answersByOrder,
  readOnly = false,
  maxRows,
  onColumnCountChange
}: {
  cards: HskEntry[];
  isMobile?: boolean;
  answersByOrder?: Record<number, boolean | null | undefined>;
  readOnly?: boolean;
  maxRows?: number;
  onColumnCountChange?: (columnCount: number) => void;
}) => {
  const classes = useStyles();
  const gridRef = useRef<HTMLDivElement>(null);
  const [columnCount, setColumnCount] = useState(isMobile ? 5 : 8);
  const minTile = isMobile ? MOBILE_MIN_TILE : DESKTOP_MIN_TILE;
  const gap = isMobile ? MOBILE_GAP : DESKTOP_GAP;
  const visibleCount =
    maxRows === undefined ? cards.length : Math.min(cards.length, columnCount * maxRows);

  useEffect(() => {
    onColumnCountChange?.(columnCount);
  }, [columnCount, onColumnCountChange]);

  useEffect(() => {
    const node = gridRef.current;
    if (!node) {
      return;
    }

    const updateColumns = () => {
      setColumnCount(getColumnCount(node.clientWidth, minTile, gap));
    };

    updateColumns();
    const observer = new ResizeObserver(updateColumns);
    observer.observe(node);
    return () => observer.disconnect();
  }, [gap, minTile]);

  return (
    <div ref={gridRef} className={`${classes.grid} ${isMobile ? classes.gridMobile : ""}`}>
      {cards.slice(0, visibleCount).map((card) => (
        <CompactCard
          key={card.order}
          card={card}
          answersByOrder={answersByOrder}
          readOnly={readOnly}
        />
      ))}
    </div>
  );
};

export default CompactCardGrid;
